// The query string of /news, read and written in one place:
//   tab      latest | feed | saved          (default latest)
//   sources  origin:source_key, comma-separated: the filter of Ultime news
//            (the checkboxes of the panel, a clicked node or chip); absent
//            means every source, `none` means no source
//   months   YYYY-MM, comma-separated       (News salvate: whole months)
//   weeks    YYYY-MM-Www, comma-separated   (News salvate: single weeks of a month
//            not taken whole; the ISO week clipped to the month, as the api names it)
//   feed     the id of one of the user's feeds, or `preferiti` (Il mio feed)
// Pure functions over URLSearchParams, so the pages and the tests share them.

export type NewsTab = "latest" | "feed" | "saved";

export interface NewsParams {
  tab: NewsTab;
  /** null means every source; an empty list means none (?sources=none). */
  sources: string[] | null;
  /** Whole months; null means none named (with `weeks` null too: every month). */
  months: string[] | null;
  /** Single weeks (YYYY-MM-Www) of months not taken whole; null means none named. */
  weeks: string[] | null;
  feed: string | null;
}

export const DEFAULT_NEWS_PARAMS: NewsParams = { tab: "latest", sources: null, months: null, weeks: null, feed: null };

/** The filter of News salvate: whole months and single weeks; both null means every bookmark. */
export type SavedFilter = Pick<NewsParams, "months" | "weeks">;

/** The value of ?sources= when nothing is ticked. */
export const NO_SOURCES = "none";

const TABS: readonly NewsTab[] = ["latest", "feed", "saved"];

const SOURCE_KEY = /^(orchestra|coro):[A-Za-z0-9_.-]{1,100}$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const WEEK = /^\d{4}-(0[1-9]|1[0-2])-W(0[1-9]|[1-4]\d|5[0-3])$/;

function list(raw: string | null, valid: RegExp): string[] | null {
  if (raw === null) return null;
  const values = raw
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0 && valid.test(v));
  return values.length > 0 ? [...new Set(values)] : null;
}

/** ?sources=: `none` is an empty filter; a list of keys, or nothing valid, as `list`. */
function sources(raw: string | null): string[] | null {
  return raw?.trim() === NO_SOURCES ? [] : list(raw, SOURCE_KEY);
}

function oneOf<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((v) => v === raw) ?? fallback;
}

export function readNewsParams(search: URLSearchParams): NewsParams {
  return {
    tab: oneOf(search.get("tab"), TABS, DEFAULT_NEWS_PARAMS.tab),
    sources: sources(search.get("sources")),
    months: list(search.get("months"), MONTH),
    weeks: list(search.get("weeks"), WEEK),
    feed: search.get("feed")?.trim() || null,
  };
}

/** The query string of the given state; defaults are left out, so /news stays /news. */
export function writeNewsParams(params: Partial<NewsParams>): URLSearchParams {
  const out = new URLSearchParams();
  if (params.tab && params.tab !== DEFAULT_NEWS_PARAMS.tab) out.set("tab", params.tab);
  if (params.sources) out.set("sources", params.sources.length > 0 ? params.sources.join(",") : NO_SOURCES);
  if (params.months && params.months.length > 0) out.set("months", params.months.join(","));
  if (params.weeks && params.weeks.length > 0) out.set("weeks", params.weeks.join(","));
  if (params.feed) out.set("feed", params.feed);
  return out;
}

/** The current params with some of them changed: for a link or a navigate. */
export function withNewsParams(search: URLSearchParams, change: Partial<NewsParams>): URLSearchParams {
  return writeNewsParams({ ...readNewsParams(search), ...change });
}
